import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Search, Loader2 } from "lucide-react";

const TYPE_CHIP = {
  klant: { label: "Klant", cls: "bg-blue-50 text-blue-700 border-blue-200", route: (id) => `/customers/${id}` },
  lead: { label: "Lead", cls: "bg-amber-50 text-amber-700 border-amber-200", route: (id) => `/sales/leads/${id}` },
  deal: { label: "Deal", cls: "bg-emerald-50 text-emerald-700 border-emerald-200", route: (id) => `/sales/deals/${id}` },
};

const RECENT_KEY = "dcs_recent_searches";
const loadRecents = () => {
  try { return JSON.parse(sessionStorage.getItem(RECENT_KEY)) || []; } catch { return []; }
};

export const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [recents, setRecents] = useState(loadRecents);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(-1);
  const inputRef = useRef(null);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === "Escape") setOpen(false);
    };
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onClick);
    return () => { window.removeEventListener("keydown", onKey); window.removeEventListener("mousedown", onClick); };
  }, []);

  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) { setResults([]); setActive(-1); return; }
    setBusy(true);
    const t = setTimeout(() => {
      api.get(`/search?q=${encodeURIComponent(s)}`)
        .then(({ data }) => { setResults(data.results || []); setOpen(true); setActive(-1); })
        .catch(() => {})
        .finally(() => setBusy(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const showRecents = q.trim().length < 2;
  const items = showRecents ? recents : results;

  const go = useCallback((r) => {
    const next = [{ type: r.type, id: r.id, name: r.name, sub: r.sub },
      ...recents.filter((x) => !(x.type === r.type && x.id === r.id))].slice(0, 5);
    setRecents(next);
    try { sessionStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch {}
    setOpen(false); setQ("");
    navigate(TYPE_CHIP[r.type].route(r.id));
  }, [navigate, recents]);

  const onInputKey = (e) => {
    if (!open || !items.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, items.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Enter" && active >= 0) { e.preventDefault(); go(items[active]); }
  };

  return (
    <div ref={boxRef} className="relative w-full max-w-md" data-testid="global-search">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onInputKey}
          onFocus={() => setOpen(q.trim().length >= 2 || recents.length > 0)}
          placeholder="Zoek klant, lead of deal…  (Ctrl+K)"
          data-testid="global-search-input"
          className="h-9 w-full rounded-full border border-slate-200 bg-slate-50 pl-9 pr-9 text-sm text-slate-800
            placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:bg-white transition-colors" />
        {busy && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-slate-400" />}
      </div>
      {open && (showRecents ? recents.length > 0 : true) && (
        <div data-testid="global-search-results"
          className="absolute left-0 right-0 top-11 z-50 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden">
          {showRecents && (
            <p data-testid="global-search-recent-header"
              className="px-3.5 pt-2.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              Recent gezocht
            </p>
          )}
          {!showRecents && results.length === 0 && <p className="px-4 py-3 text-sm text-slate-400">Geen resultaten voor "{q}"</p>}
          {items.map((r, i) => {
            const chip = TYPE_CHIP[r.type];
            return (
              <button key={`${r.type}-${r.id}`} onClick={() => go(r)}
                data-testid={showRecents ? `global-search-recent-${i}` : `global-search-result-${i}`}
                className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left transition-colors ${
                  i === active ? "bg-blue-50" : "hover:bg-slate-50"}`}>
                <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${chip.cls}`}>{chip.label}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-800">{r.name}</span>
                  {r.sub && <span className="block truncate text-xs text-slate-400">{r.sub}</span>}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
